import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { ApprovedRepaymentSchedule } from '../../../components/ApprovedRepaymentSchedule';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useAuth } from '../../../features/auth/auth-context';
import { customersApi } from '../../../features/customers/customers.api';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatBusinessDate } from '../../../lib/format-date';
import { routeTo } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function ContractDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { can } = useAuth();
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (signatureUrl !== null) URL.revokeObjectURL(signatureUrl);
    };
  }, [signatureUrl]);

  const contract = useQuery({
    queryKey: ['contracts', id],
    queryFn: () => applicationsApi.get(id),
  });

  const mandate = useQuery({
    queryKey: ['customer', 'contracts', id, 'mandate'],
    queryFn: () => mandatesApi.getContractMandate(id),
    enabled: contract.isSuccess && contract.data.status === 'AWAITING_MANDATE',
  });

  const schedule = useQuery({
    queryKey: ['contracts', id, 'schedule'],
    queryFn: () => applicationsApi.getSchedule(id),
    enabled: contract.isSuccess,
  });

  if (contract.isPending) {
    return <PageSpinner label="Loading contract" />;
  }

  if (contract.isError) {
    return <Alert tone="error">{toDisplayMessage(contract.error)}</Alert>;
  }

  const data = contract.data;
  const isLegacyAcceptedContract =
    data.acceptanceSnapshot == null &&
    [
      'ACCEPTED',
      'AWAITING_MANDATE',
      'MANDATE_ACTIVE',
      'ACTIVE',
      'SUSPENDED',
      'COMPLETED',
      'CANCELLED',
      'DEFAULTED',
    ].includes(data.status);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{data.contractReference ?? 'Contract'}</h1>
          <p className={styles.pageSubtitle}>
            {data.acceptanceSnapshot == null
              ? 'Legacy contract record'
              : `Accepted ${new Date(data.acceptanceSnapshot.acceptedAt).toLocaleString()} · Document ${data.acceptanceSnapshot.templateVersion}`}
          </p>
        </div>
        <StatusBadge status={data.status} />
        <Button variant="secondary" onClick={() => window.print()}>
          Print / save contract
        </Button>
      </header>

      {isLegacyAcceptedContract && (
        <Alert tone="warning">
          This contract predates immutable acceptance snapshots. Current profile data is not
          presented as historical acceptance evidence. The acceptance date, document hash and
          signature are unavailable; none has been reconstructed.
        </Alert>
      )}

      {data.status === 'AWAITING_MANDATE' && can(PERMISSIONS.MANDATES_CREATE) && (
        <section className={styles.card} aria-labelledby="mandate-setup-heading">
          <h2 className={styles.cardTitle} id="mandate-setup-heading">
            Direct Debit
          </h2>

          {mandate.isFetching && <PageSpinner label="Checking mandate" />}
          {mandate.isError && <Alert tone="error">{toDisplayMessage(mandate.error)}</Alert>}

          {mandate.isSuccess && mandate.data === null && (
            <div className={styles.actions}>
              <Link to={routeTo.customerMandateSetup(id)}>Set up Direct Debit</Link>
            </div>
          )}

          {mandate.isSuccess && mandate.data !== null && (
            <div className={styles.actions}>
              <Link to={routeTo.customerMandate(mandate.data.id)}>View mandate</Link>
            </div>
          )}
        </section>
      )}

      <section className={styles.card} aria-labelledby="contract-details-heading">
        <h2 className={styles.cardTitle} id="contract-details-heading">
          Terms
        </h2>

        {data.acceptanceSnapshot != null && (
          <>
            <dl className={styles.definitionList}>
              <dt>Contracting customer</dt>
              <dd>{data.acceptanceSnapshot.party.legalName}</dd>
              <dt>Verified email at acceptance</dt>
              <dd>{data.acceptanceSnapshot.party.verifiedEmail}</dd>
              <dt>Phone at acceptance</dt>
              <dd>{data.acceptanceSnapshot.party.phoneNumber}</dd>
              <dt>Installation / delivery address</dt>
              <dd>
                {data.acceptanceSnapshot.party.installationAddress.addressLine1},{' '}
                {data.acceptanceSnapshot.party.installationAddress.city},{' '}
                {data.acceptanceSnapshot.party.installationAddress.stateCode}
              </dd>
              <dt>Document SHA-256</dt>
              <dd className={styles.hashValue}>{data.acceptanceSnapshot.contentSha256}</dd>
              <dt>Signature evidence</dt>
              <dd>
                {data.acceptanceSnapshot.signature === null
                  ? 'Unavailable for this legacy acceptance'
                  : `Attached · ${data.acceptanceSnapshot.signature.mimeType} · ${(
                      data.acceptanceSnapshot.signature.byteSize / 1_000_000
                    ).toFixed(2)} MB`}
              </dd>
            </dl>
            <pre className={styles.contractDocument}>
              {data.acceptanceSnapshot.canonicalContent}
            </pre>
            {data.acceptanceSnapshot.signature !== null && (
              <div className={styles.acceptancePanel}>
                <h3>Bound signature evidence</h3>
                <p>
                  This is the exact immutable signature object attached to this accepted contract.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const signature = data.acceptanceSnapshot?.signature;
                    if (signature === null || signature === undefined) return;

                    void customersApi
                      .previewDocument(signature.documentId)
                      .then((blob) => {
                        if (signatureUrl !== null) URL.revokeObjectURL(signatureUrl);
                        setSignatureUrl(URL.createObjectURL(blob));
                        setSignatureError(null);
                      })
                      .catch((error: unknown) => setSignatureError(toDisplayMessage(error)));
                  }}
                >
                  Preview attached signature
                </Button>
                {signatureError !== null && <Alert tone="error">{signatureError}</Alert>}
                {signatureUrl !== null && (
                  <div className={styles.signaturePreview}>
                    <img src={signatureUrl} alt="Signature attached to accepted contract" />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <dl className={styles.definitionList}>
          <dt>Product</dt>
          <dd>
            {data.acceptanceSnapshot?.commercial.productName ?? data.productNameSnapshot ?? '—'}
          </dd>
          <dt>Final product price</dt>
          <dd>
            {formatNgn(data.acceptanceSnapshot?.commercial.productPrice ?? data.productPrice)}
          </dd>
          <dt>Discount</dt>
          <dd>
            {formatNgn(data.acceptanceSnapshot?.commercial.discountAmount ?? data.discountAmount)}
          </dd>
          <dt>Financed amount</dt>
          <dd>
            {formatNgn(data.acceptanceSnapshot?.commercial.financedAmount ?? data.financedAmount)}
          </dd>
          <dt>Deposit</dt>
          <dd>
            {formatNgn(data.acceptanceSnapshot?.commercial.depositAmount ?? data.depositAmount)}
          </dd>
          <dt>Regular instalment</dt>
          <dd>
            {formatNgn(
              data.acceptanceSnapshot?.commercial.instalmentAmount ?? data.instalmentAmount,
            )}
          </dd>
          <dt>Final instalment</dt>
          <dd>
            {formatNgn(
              data.acceptanceSnapshot?.commercial.finalInstalmentAmount ??
                data.finalInstalmentAmount,
            )}
          </dd>
          <dt>Total repayable</dt>
          <dd>
            {formatNgn(
              data.acceptanceSnapshot?.commercial.totalRepayableAmount ?? data.totalRepayableAmount,
            )}
          </dd>
          <dt>Repayment frequency</dt>
          <dd>
            {data.acceptanceSnapshot?.commercial.repaymentFrequency ??
              data.repaymentFrequency ??
              '—'}
          </dd>
          <dt>Accepted</dt>
          <dd>
            {data.acceptanceSnapshot != null
              ? new Date(data.acceptanceSnapshot.acceptedAt).toLocaleString()
              : isLegacyAcceptedContract
                ? 'Historical acceptance date unavailable'
                : 'Not yet'}
          </dd>
        </dl>
      </section>

      {data.acceptanceSnapshot?.commercial.repaymentSchedule != null && (
        <section className={styles.card}>
          <ApprovedRepaymentSchedule
            rows={data.acceptanceSnapshot.commercial.repaymentSchedule}
            title="Agreed schedule at acceptance"
          />
        </section>
      )}
      <section className={styles.card} aria-labelledby="schedule-heading">
        <h2 className={styles.cardTitle} id="schedule-heading">
          Repayment schedule
        </h2>
        <p>
          Live payment status. Paid and due instalments remain visible; the agreed contract document
          does not change.
        </p>

        {schedule.isPending && <PageSpinner label="Loading schedule" />}
        {schedule.isError && <Alert tone="error">{toDisplayMessage(schedule.error)}</Alert>}

        {schedule.isSuccess && schedule.data.length === 0 && (
          <p className={styles.empty}>Schedule not yet generated.</p>
        )}

        {schedule.isSuccess && schedule.data.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Repayment schedule">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Due date</th>
                  <th scope="col">Instalment</th>
                  <th scope="col">Opening balance</th>
                  <th scope="col">Closing balance</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {schedule.data.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.sequenceNumber}</td>
                    <td>{formatBusinessDate(entry.dueDate)}</td>
                    <td>{formatNgn(entry.instalmentAmount)}</td>
                    <td>{formatNgn(entry.openingBalance)}</td>
                    <td>{formatNgn(entry.closingBalance)}</td>
                    <td>
                      <StatusBadge status={entry.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
